import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  Stack,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';

import {
  getCourseTemplateByIdAsAdmin,
  getCourseTemplatesState,
  resetAdminCourseTemplate,
} from '../../../../../../store/V2/CourseTemplatesSlice';
import { getCoursesState } from '../../../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../../../hooks';
import BasicLoader from '../../../../../../components/Loaders/BasicLoader';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';

interface ViewTemplateModalProps {
  open: boolean;
  onClose: () => void;
}

type TStep = {
  index: number;
  key: 'step1' | 'step2' | 'step3' | 'step4';
  label: string;
};

type TState = {
  selectedStep: TStep;
};

const defaultSteps: TStep[] = [
  { index: 0, key: 'step1', label: 'Basic Details' },
  { index: 1, key: 'step2', label: 'User Details' },
  { index: 2, key: 'step3', label: 'Class Details' },
  { index: 3, key: 'step4', label: 'Options' },
];

const initialState: TState = {
  selectedStep: defaultSteps[0],
};

const ViewTemplateModal: React.FC<ViewTemplateModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const courseTemplatesState = useAppSelector(getCourseTemplatesState);

  // State
  const [state, setState] = useState({ ...initialState });

  const courseData = coursesState.adminCourse;
  const courseTemplateData = courseTemplatesState.adminCourseTemplate;
  let steps = [...defaultSteps];

  if (!courseData?.isOneToOne) {
    steps = steps
      .filter((item) => item.key !== 'step2')
      .map((item, index) => ({
        ...item,
        index,
      }));
  }

  useEffect(() => {
    if (courseData && courseData.template) {
      dispatch(
        getCourseTemplateByIdAsAdmin({
          id: courseData.template,
          isSectionsAllowed: true,
        })
      );
    }

    let newSelectedStep: TStep['key'] = 'step1';

    if (courseData && courseData.templateSteps) {
      if (courseData.templateSteps.step3 === 'completed') {
        newSelectedStep = 'step4';
      } else if (courseData.templateSteps.step2 === 'completed') {
        newSelectedStep = 'step3';
      } else if (courseData.templateSteps.step1 === 'completed') {
        newSelectedStep = 'step2';
      }
    }

    if (newSelectedStep) {
      changeStep(newSelectedStep);
    }

    return () => {
      dispatch(resetAdminCourseTemplate());
    };
  }, []);

  const changeStep = (key: TStep['key']) => {
    const nextStep = steps.find((item) => item.key === key);

    if (nextStep) {
      setState((prevState) => ({
        ...prevState,
        selectedStep: nextStep,
      }));
    }
  };

  const renderSteps = () => {
    let element: React.ReactElement | null = null;

    switch (state.selectedStep.key) {
      case 'step1':
        element = (
          <Step1
            moveToNextStep={() => {
              if (courseData?.isOneToOne) {
                changeStep('step2');
              } else {
                changeStep('step3');
              }
            }}
          />
        );
        break;

      case 'step2':
        element = (
          <Step2
            moveToPreviousStep={() => changeStep('step1')}
            moveToNextStep={() => changeStep('step3')}
          />
        );
        break;

      case 'step3':
        element = (
          <Step3
            moveToNextStep={() => changeStep('step4')}
            moveToPreviousStep={() => {
              if (courseData?.isOneToOne) {
                changeStep('step2');
              } else {
                changeStep('step1');
              }
            }}
          />
        );
        break;

      case 'step4':
        element = (
          <Step4
            moveToPreviousStep={() => changeStep('step3')}
            onFinish={() => onClose()}
          />
        );
        break;

      default:
        element = null;
        break;
    }

    return element;
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogContent>
        {!courseTemplateData && courseTemplatesState.status === 'loading' ? (
          <div
            style={{
              minHeight: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BasicLoader />
          </div>
        ) : (
          <>
            <Stack mb="30px">
              <Stepper activeStep={state.selectedStep.index}>
                {steps.map((step) => {
                  const isCompleted = step.index < state.selectedStep.index;

                  return (
                    <Step key={step.label} completed={isCompleted}>
                      <StepLabel>{step.label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Stack>
            <Stack>{renderSteps()}</Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewTemplateModal;
